import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { LoadingState, LoadingStates } from 'globals/types';
import { temporaryEndpoints } from 'http/endpoints';
import { isStoreInitialized, performSetAction } from 'models/utils/utils';
import { Details, DetailsProps } from './details/details';

interface PlanDetailsRowProps {
  name: string;
  value: string;
}

export interface PlanDetailsProps {
  data?: DetailsProps;
  status: LoadingState;
}

export const PlanDetails = types
  .model({
    data: types.optional(Details, {}),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: PlanDetailsProps): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...props,
      });
    },
    updateStatus(status: LoadingState): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        status,
      });
    },
    setData(props: Partial<PlanDetailsProps>): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        data: { ...self.data, ...props },
      });
    },
  }))
  .actions((self) => ({
    async setStore(): Promise<void> {
      await performSetAction({
        store: self,
        endpoint: temporaryEndpoints.billings.planDetails,
      });
    },
  }))
  .actions((self) => ({
    async init(): Promise<void> {
      const { status } = getSnapshot(self);
      if (isStoreInitialized(status)) return;
      await self.setStore();
    },
    async update(): Promise<void> {
      await self.setStore();
    },
  }))
  .views((self) => ({
    getPlanDetailsArray(): PlanDetailsRowProps[] {
      const { data } = self;
      const snap = getSnapshot(data);
      return Object.entries(snap).map(([name, value]) => ({
        name,
        value,
      }));
    },
    getPlanDetailsSnapshot(): DetailsProps {
      const { data } = self;
      return getSnapshot(data);
    },
  }));

export const planDetails = PlanDetails.create();
export type PlanDetailsSnapshot = SnapshotIn<typeof PlanDetails>;
export type PlanDetailsInstance = Instance<typeof PlanDetails>;
export default planDetails;
